import { Grid, GridItem, InputLeftElement } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React, { useCallback, useEffect } from "react";
import { QuoteApi } from "../../api/QuoteApi";
import useGetUserRole from "../../hooks/useGetUserRole";
import { UserRole } from "../../lib/models/enums";
import { selectRentalProduct } from "../../lib/models/rental/rental-form-selects.model";
import { RentalForm } from "../../lib/models/rental/rental-form.model";
import { selectRentalPremierProduct } from "../../lib/models/rental/rental-premier-form-selects.model";
import {
  adminSelectChannel,
  brokerRetailSelectChannel,
  selectChannel,
  thirdPartyRetailSelectChannel,
} from "../../lib/models/short-term/short-term-form-selects.model";
import { QuoteType } from "../../lib/types/QuoteType";
import { SelectItem } from "../../lib/types/SelectItem";
import {
  brokerRetailUsers,
  isInternalUser,
  thirdPartyRetailUsers,
} from "../../lib/UserPermissions";
import FormSectionHeader from "../common-quote/FormSectionHeader";
import CheckboxField from "../form-fields/CheckboxField";
import SelectField from "../form-fields/SelectField";
import TextInputField from "../form-fields/TextInputField";
import ShowForRole from "../ShowForRole";

function RentalBorrowerInformationForm({
  quoteType,
}: {
  quoteType: QuoteType;
  initialQuoteType: QuoteType;
}) {
  const { setFieldValue, setFieldTouched, initialValues, values } =
    useFormikContext<RentalForm>();

  const _isMounted = React.useRef(true);
  const firstUpdate = React.useRef(true);

  const { productType } = initialValues;

  const role = useGetUserRole();

  const [loanProducts, setLoanProducts] =
    React.useState<SelectItem[]>(selectRentalProduct);

  const [hasIntialValue, setHasInitialValue] = React.useState<boolean>(true);

  const [wholesalePartners, setWholesalePartners] = React.useState<
    SelectItem[]
  >([]);

  const setSubmissionType = (channel: string) => {
    values.submissionType = channel;
  };

  const getChannelSelectByRole = (role: UserRole) => {
    if (!role) return selectChannel;

    if (role === "Administrator") return adminSelectChannel;

    if (thirdPartyRetailUsers.includes(role))
      return thirdPartyRetailSelectChannel;

    if (brokerRetailUsers.includes(role)) return brokerRetailSelectChannel;

    return selectChannel;
  };

  const getWholesaleSelect = useCallback(() => {
    QuoteApi.getWholesalePartners()
      .then((res) => {
        const partners = res.data;
        const partnersArray: SelectItem[] = partners.map((partner) => {
          return {
            label: partner.wholesalePartner,
            value: partner.wholesalePartner,
          };
        });
        if (_isMounted.current || firstUpdate.current) {
          partnersArray && setWholesalePartners(partnersArray);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }, [setWholesalePartners]);

  useEffect(() => {
    if (!productType) return;

    if (quoteType === "Premier") {
      setHasInitialValue(
        !!selectRentalPremierProduct.find((x) => x.value === productType)
      );
      setLoanProducts(selectRentalPremierProduct);
    } else {
      setHasInitialValue(
        !!selectRentalProduct.find((x) => x.value === productType)
      );
      setLoanProducts(selectRentalProduct);
    }
  }, [quoteType, productType]);

  useEffect(() => {
    if (hasIntialValue) {
      setFieldValue("productType", productType);
    } else {
      setFieldValue("productType", "");
      setFieldTouched("productType", true);
    }
  }, [hasIntialValue, productType, setFieldTouched, setFieldValue]);

  useEffect(() => {
    return () => {
      _isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    getWholesaleSelect();
  }, [getWholesaleSelect]);

  useEffect(() => {
    if (_isMounted.current || firstUpdate.current) {
      getWholesaleSelect();
    }
  }, [getWholesaleSelect]);

  return (
    <Grid
      templateColumns="repeat(12, minmax(0, 1fr))"
      gap={{ base: 6, md: 8, lg: 8 }}
    >
      <GridItem colSpan={12}>
        <FormSectionHeader
          id="borrower-information"
          label="Borrower Information"
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, md: 12, xl: 6 }}>
        <TextInputField label="Quote Name" name="quoteName" />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField
          label="Number of Properties"
          type="number"
          name="numOfProperties"
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <SelectField
          label="Loan Product"
          name="productType"
          placeholder="Select product type"
          data={loanProducts}
        />
      </GridItem>

      {isInternalUser(role) ? (
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <SelectField
            label="Submission Type"
            name="submissionType"
            placeholder="Select submission type"
            data={getChannelSelectByRole(role)}
          />
        </GridItem>
      ) : (
        setSubmissionType("Broker")
      )}

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField label="Borrowing Entity" name="borrowingEntity" />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField
          label="Borrower Net Worth"
          name="borrowerNetWorth"
          type="number"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField label="Credit Score" type="number" name="creditScore" />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField
          label="Number of Rental Units Owned"
          type="number"
          name="sreoPropertyNumber"
        />
      </GridItem>

      <ShowForRole roles={["Broker Manager"]}>
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <SelectField
            label="Wholesale Partner"
            name="wholesalePartner"
            placeholder="Select Wholesale Partner"
            data={wholesalePartners}
          />
        </GridItem>
      </ShowForRole>

      <GridItem alignSelf="center" colSpan={{ base: 12, lg: 6 }}>
        <CheckboxField label="Is borrower a US Citizen?" name="usCitizen" />
      </GridItem>
    </Grid>
  );
}

export default RentalBorrowerInformationForm;
